import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Typography,
    Box,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import apiCall from '../../../../../../app/API/apiService';
import apiEndpoints from '../../../../../../app/API/apiEndpoints';

const CandidateInterviewDetails = () => {
    const [interviews, setInterviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const [copiedFeedbackId, setCopiedFeedbackId] = useState(null);
    
    const location = useLocation();
    const navigate = useNavigate();

    // Add this constant definition near the top of your component function
    const strongStyle = {
        fontWeight: 'bold',
        marginRight: '4px',
        color: '#333'
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const candidateId = searchParams.get("candidate_id");
        const jobOpeningId = searchParams.get("job_opening_id");

        // Store parameters in local storage if they exist
        if (candidateId && jobOpeningId) {
            localStorage.setItem("candidate_id", candidateId);
            localStorage.setItem("job_opening_id", jobOpeningId);
            fetchInterviewDetails(candidateId, jobOpeningId);
        } else {
            // Retrieve from local storage if not present in URL
            const storedCandidateId = localStorage.getItem("candidate_id");
            const storedJobOpeningId = localStorage.getItem("job_opening_id");
            
            if (storedCandidateId && storedJobOpeningId) {
                // Update URL with stored values
                window.history.replaceState(
                    null,
                    "",
                    `?candidate_id=${storedCandidateId}&job_opening_id=${storedJobOpeningId}`
                );
                fetchInterviewDetails(storedCandidateId, storedJobOpeningId);
            }
        }
    }, [location.search]);

    const fetchInterviewDetails = async (candidateId, jobOpeningId) => {
        try {
            const response = await apiCall(
                "get",
                `${apiEndpoints.CANDIDATE_DETAILS}/candidate/${candidateId}/jobopening/${jobOpeningId}/interviews`
            );
            if (response && response.interviews) {
                setInterviews(response.interviews);
            } else {
                setInterviews([]);
            }
            setError(null);
        } catch (error) {
            console.error("Error fetching interview details:", error);
            setError("Failed to fetch interview details. Please try again later.");
            setInterviews([]);
        } finally {
            setIsLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

    const handleViewStatusHistory = (interview) => {
        setSelectedInterview(interview);
        setStatusHistoryOpen(true);
    };

    const handleCopyFeedback = (feedback, roundId) => {
        if (feedback) {
            navigator.clipboard
                .writeText(feedback)
                .then(() => {
                    setCopiedFeedbackId(roundId);
                    setTimeout(() => setCopiedFeedbackId(null), 2000);
                })
                .catch((err) => {
                    console.error("Failed to copy text: ", err);
                });
        } else {
            console.error("No feedback to copy");
        }
    };

    const renderFeedback = (interview) => {
        if (!interview.feedback) {
            return (
                <Typography color="text.secondary">
                    No feedback available for this interview
                </Typography>
            );
        }

        return (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                    {/* Questions Stats Header */}
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                        <strong style={{ marginRight: 16 }}>Feedback:</strong>
                        <div style={{ display: "flex", gap: "16px" }}>
                            <div>
                                <strong>Questions Asked:</strong>{" "}
                                {interview?.feedback?.questions_asked != null &&
                                interview.feedback.questions_asked >= 0 ? (
                                    <span>{interview.feedback.questions_asked}</span>
                                ) : (
                                    <span style={{ color: "#888" }}>0</span>
                                )}
                            </div>
                            <div>
                                <strong>Questions Answered:</strong>{" "}
                                {interview?.feedback?.questions_answered != null &&
                                interview.feedback.questions_answered >= 0 ? (
                                    <span>{interview.feedback.questions_answered}</span>
                                ) : (
                                    <span style={{ color: "#888" }}>0</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Feedback Content */}
                    <div
                        style={{
                            padding: 12,
                            lineHeight: 1.8,
                            letterSpacing: 0.8,
                            maxHeight: 260,
                            border: "1px dashed gray",
                            borderRadius: 8,
                            color: "rgba(0,0,0,0.8)",
                            overflow: "auto",
                        }}
                    >
                        {interview?.feedback && Object?.keys(interview?.feedback?.evaluation || {}).length > 0 ? (
                            Object.keys(interview?.feedback?.evaluation).map((questionId, index) => {
                                const evaluation = interview?.feedback?.evaluation[questionId];
                                return (
                                <div key={index} style={{ marginBottom: 10 }}>
                                    <h4 style={{ margin: 0, fontSize: "1rem", color: "#333" }}>
                                        {evaluation.question || questionId}
                                    </h4>
                                    
                                    {evaluation?.answer !== null && (
                                        <p style={{ margin: "5px 0", color: "#555" }}>
                                            <strong style={strongStyle}><b>Answer:</b></strong>{" "}
                                            <div style={{ marginTop: "5px" }}>
                                                {formatAnswer(evaluation?.answer)}
                                            </div>
                                        </p>
                                    )}
                                    
                                    {evaluation?.corrected_answer && (
                                        <p style={{ margin: "5px 0", color: "#555" }}>
                                            <strong style={strongStyle}><b>Corrected Answer:</b></strong>{" "}
                                            <div style={{ marginTop: "5px" }}>
                                                {formatCorrectedAnswer(evaluation?.corrected_answer)}
                                            </div>
                                        </p>
                                    )}
                                    
                                    <p style={{ margin: "5px 0", color: "#555" }}>
                                        <strong><b>Feedback:</b></strong>{" "}
                                        {evaluation?.feedback || "No feedback"}
                                    </p>
                                    
                                    {evaluation?.error && (
                                        <p style={{ margin: "5px 0", color: "red" }}>
                                            <strong>Error:</strong>{" "}
                                            {evaluation?.error}
                                        </p>
                                    )}
                                    
                                    <p style={{ margin: "5px 0", color: "#555" }}>
                                        <strong><b>Score:</b></strong>{" "}
                                        {evaluation?.score}
                                    </p>
                                    <hr style={{ border: "0.5px solid #ddd", margin: "8px 0" }} />
                                </div>
                                );
                            })
                        ) : (
                            "No feedback available"
                        )}
                    </div>
                </div>

                {/* Copy Button */}
                <div style={{ marginLeft: 16 }}>
                    <IconButton
                        onClick={() => handleCopyFeedback(interview?.feedback?.feedback, interview.id)}
                        size="small"
                    >
                        <ContentCopyIcon />
                    </IconButton>
                </div>
            </div>
        );
    };

    // Function to format the answer with colored backgrounds for speakers
    const formatAnswer = (text) => {
        if (!text) return null;
        
        return text.split(/\n/).map((line, lineIndex) => {
            // Check if line starts with Interviewer: or Candidate:
            const isInterviewer = line.trim().startsWith("Interviewer:");
            const isCandidate = line.trim().startsWith("Candidate:");
            
            // Apply background color based on speaker
            const lineStyle = {
                padding: '2px 4px',
                borderRadius: '3px',
                display: 'block',
                marginBottom: '2px',
                backgroundColor: isInterviewer ? '#e3f2fd' : isCandidate ? '#fff8e1' : 'transparent'
            };
            
            return (
                <div key={lineIndex} style={lineStyle}>
                    {line}
                </div>
            );
        });
    };

    // Function to format the corrected answer with bold text and colored backgrounds for speakers
    const formatCorrectedAnswer = (text) => {
        if (!text) return null;
        
        return text.split(/\n/).map((line, lineIndex) => {
            // Check if line starts with Interviewer: or Candidate:
            const isInterviewer = line.trim().startsWith("Interviewer:");
            const isCandidate = line.trim().startsWith("Candidate:");
            
            // Apply background color based on speaker
            const lineStyle = {
                padding: '2px 4px',
                borderRadius: '3px',
                display: 'block',
                marginBottom: '2px',
                backgroundColor: isInterviewer ? '#e3f2fd' : isCandidate ? '#fff8e1' : 'transparent'
            };
            
            return (
                <div key={lineIndex} style={lineStyle}>
                    {line.split(/\*\*(.*?)\*\*/g).map((part, index) => 
                        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                    )}
                </div>
            );
        });
    };

    // Function to format transcript with colored backgrounds for speakers
    const formatTranscript = (text) => {
        if (!text) return "No transcript available";
        
        return text.split(/\n/).map((line, lineIndex) => {
            // Check if line starts with Interviewer: or Candidate:
            const isInterviewer = line.trim().startsWith("Interviewer:");
            const isCandidate = line.trim().startsWith("Candidate:");
            
            // Apply background color based on speaker
            const lineStyle = {
                padding: '2px 4px',
                borderRadius: '3px',
                display: 'block',
                marginBottom: '2px',
                backgroundColor: isInterviewer ? '#e3f2fd' : isCandidate ? '#fff8e1' : 'transparent'
            };
            
            return (
                <div key={lineIndex} style={lineStyle}>
                    {line}
                </div>
            );
        });
    };

    if (isLoading) return <Typography>Loading...</Typography>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <div>
            <Box sx={{ p: 3 }}>
                {/* Back button container with right alignment */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',  // Aligns content to the right
                    mb: 3
                }}>
                    <Button 
                        variant="contained"  // Changed from "outlined" to "contained"
                        onClick={() => navigate(-1)}
                        sx={{
                            backgroundColor: '#1976d2', // Material-UI default blue
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#1565c0', // Slightly darker blue on hover
                            }
                        }}
                    >
                        Back to Candidate Details
                    </Button>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableBody>
                            {interviews.map((interview, index) => (
                                <React.Fragment key={index}>
                                    {/* Round Header */}
                                    <TableRow>
                                        <TableCell component="th" scope="row" colSpan={6}>
                                            <Typography variant="h6" sx={{ 
                                                bgcolor: '#f5f5f5', 
                                                p: 1, 
                                                borderRadius: 1,
                                                fontWeight: 'bold'
                                            }}>
                                                Round {interview.round_no} - {interview.round_name}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {/* Interview Details */}
                                    <TableRow>
                                        <TableCell>
                                            <strong>Interview Type:</strong> {interview.interview_type}
                                        </TableCell>
                                        <TableCell>
                                            <strong>Date:</strong> {interview.date}
                                        </TableCell>
                                        <TableCell>
                                            <strong>Time:</strong> {interview.time}
                                        </TableCell>
                                        <TableCell>
                                            <strong>Status:</strong> {interview.status?.status || 'N/A'}
                                        </TableCell>
                                    </TableRow>

                                    {/* Recording URL Section */}
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <strong>Recording URL:</strong>
                                            <Box sx={{ display: "flex", flexDirection: "column", marginTop: 1 }}>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {Array.isArray(interview.recording_url) && interview.recording_url.length > 0 ? (
                                                        interview.recording_url.map((url, index) => (
                                                            <a 
                                                                key={index} 
                                                                href={url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                style={{ 
                                                                    color: '#1976d2',
                                                                    textDecoration: 'underline',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {`Recording ${index + 1}`}
                                                            </a>
                                                        ))
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {/* Feedback Section */}
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            {renderFeedback(interview)}
                                        </TableCell>
                                    </TableRow>

                                    {/* Transcript Section */}
                                    {interview.transcript && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                        Interview Transcript
                                                    </Typography>
                                                    <div style={{ 
                                                        padding: 12,
                                                        lineHeight: 1.8,
                                                        letterSpacing: 0.8,
                                                        maxHeight: 220,
                                                        border: "1px dashed gray",
                                                        borderRadius: 8,
                                                        marginTop: 12,
                                                        color: "rgba(0,0,0,0.8)",
                                                        overflow: "auto",
                                                    }}>
                                                        {formatTranscript(interview.transcript)}
                                                    </div>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Status History Dialog */}
            <Dialog open={statusHistoryOpen} onClose={() => setStatusHistoryOpen(false)}>
                <DialogTitle>Status History</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedInterview?.status_history.map((status, index) => (
                            <ListItem key={index}>
                                <ListItemText
                                    primary={`Status: ${status.status}`}
                                    secondary={
                                        <>
                                            <Typography component="span" variant="body2">
                                                Timestamp: {formatTimestamp(status.timestamp)}
                                            </Typography>
                                            <br />
                                            <Typography component="span" variant="body2">
                                                Remarks: {status.remarks || 'No remarks'}
                                            </Typography>
                                            <br />
                                            <Typography component="span" variant="body2">
                                                Updated by: {status.user_name} (ID: {status.user_id})
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusHistoryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CandidateInterviewDetails; 